/**
 * NotificationBell — Sidebar bell icon with unread badge and popover inbox.
 * Shows in-app notifications with Unread/Read tabs, mark-as-read, and dismiss.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ArrowLeftRight, Users, Calendar, MapPin, UserPlus, Check, X, CheckCheck } from 'lucide-react';
import { useNotifications, type InAppNotification } from '../hooks/useNotifications';

// ── Helpers ────────────────────────────────────────────────────────────────

function relativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate + (isoDate.endsWith('Z') ? '' : 'Z')).getTime();
  const diffSec = Math.floor((now - then) / 1000);

  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 172800) return 'Yesterday';
  const d = new Date(then);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TYPE_ICON: Record<string, typeof Bell> = {
  swap_request: ArrowLeftRight,
  swap_update: ArrowLeftRight,
  coverage_request: Users,
  coverage_filled: Users,
  coverage_officer: Users,
  schedule_change: Calendar,
  geo_clock: MapPin,
  open_shift: UserPlus,
};

function getNavTarget(type: string, data: Record<string, unknown> | null): string {
  if (data?.url && typeof data.url === 'string') return data.url;
  if (type.startsWith('swap')) return '/swaps';
  if (type.startsWith('coverage')) return '/swaps';
  if (type === 'schedule_change') return '/schedule';
  if (type === 'open_shift') return '/swaps';
  return '/';
}

// ── Component ──────────────────────────────────────────────────────────────

type Tab = 'unread' | 'read';

export default function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, fetchNotifications, markAsRead, markAllAsRead, dismissNotification } =
    useNotifications();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('unread');
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Fetch when opening or switching tabs
  const handleOpen = useCallback(() => {
    const next = !open;
    setOpen(next);
    if (next) {
      setTab('unread');
      fetchNotifications('all');
    }
  }, [open, fetchNotifications]);

  const handleTabChange = useCallback((t: Tab) => {
    setTab(t);
  }, []);

  const handleNotificationClick = useCallback(
    (n: InAppNotification) => {
      if (!n.read) markAsRead(n.id);
      setOpen(false);
      navigate(getNavTarget(n.type, n.data));
    },
    [markAsRead, navigate],
  );

  const handleMarkAllRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  const filtered = notifications.filter((n) => (tab === 'unread' ? !n.read : n.read));

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        type="button"
        onClick={handleOpen}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        className="relative p-2 rounded-lg text-content-secondary hover:bg-surface-sunken
                   hover:text-content-primary transition-colors duration-150 cursor-pointer"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center
                       min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none
                       text-white bg-danger-500 rounded-full"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Popover */}
      {open && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                     w-[340px] max-h-[480px] flex flex-col
                     bg-surface-raised border border-edge-default rounded-xl
                     shadow-xl z-[70] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-edge-muted">
            <h3 className="text-sm font-semibold text-content-primary">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-[11px] text-action-primary
                           hover:text-action-primary-hover cursor-pointer bg-transparent border-none"
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-edge-muted">
            {(['unread', 'read'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => handleTabChange(t)}
                className={`flex-1 py-2 text-xs font-medium uppercase tracking-wider
                           transition-colors duration-150 cursor-pointer border-none
                           ${
                             tab === t
                               ? 'text-action-primary border-b-2 border-action-primary bg-transparent'
                               : 'text-content-tertiary hover:text-content-secondary bg-transparent'
                           }`}
              >
                {t === 'unread' ? `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}` : 'Read'}
              </button>
            ))}
          </div>

          {/* Notification list */}
          <div className="flex-1 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-xs text-content-tertiary">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-1">
                <Bell className="h-5 w-5 text-content-tertiary/40" />
                <span className="text-xs text-content-tertiary">
                  {tab === 'unread' ? "You're all caught up" : 'No read notifications'}
                </span>
              </div>
            ) : (
              filtered.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onClick={() => handleNotificationClick(n)}
                  onMarkRead={() => markAsRead(n.id)}
                  onDismiss={() => dismissNotification(n.id)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Notification Item ──────────────────────────────────────────────────────

function NotificationItem({
  notification,
  onClick,
  onMarkRead,
  onDismiss,
}: {
  notification: InAppNotification;
  onClick: () => void;
  onMarkRead: () => void;
  onDismiss: () => void;
}) {
  const Icon = TYPE_ICON[notification.type] ?? Bell;

  return (
    <div
      className={`group flex gap-3 px-4 py-3 cursor-pointer transition-colors duration-100
                  hover:bg-surface-sunken border-b border-edge-muted/50 last:border-b-0
                  ${!notification.read ? 'bg-action-primary/[0.03]' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick();
      }}
    >
      {/* Type icon */}
      <div
        className={`mt-0.5 flex items-center justify-center w-7 h-7 rounded-full shrink-0
                    ${
                      !notification.read
                        ? 'bg-action-primary/10 text-action-primary'
                        : 'bg-surface-sunken text-content-tertiary'
                    }`}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={`text-xs leading-snug truncate ${
              !notification.read ? 'font-semibold text-content-primary' : 'text-content-secondary'
            }`}
          >
            {notification.title}
          </p>
          <span className="text-[10px] text-content-tertiary whitespace-nowrap shrink-0">
            {relativeTime(notification.created_at)}
          </span>
        </div>
        <p className="text-[11px] text-content-tertiary leading-snug mt-0.5 line-clamp-2">{notification.body}</p>
      </div>

      {/* Actions — visible on hover */}
      <div
        className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        {!notification.read && (
          <button
            type="button"
            onClick={onMarkRead}
            title="Mark as read"
            className="p-1 rounded text-content-tertiary hover:text-action-primary
                       hover:bg-action-primary/10 cursor-pointer bg-transparent border-none"
          >
            <Check className="h-3 w-3" />
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          title="Dismiss"
          className="p-1 rounded text-content-tertiary hover:text-danger-400
                     hover:bg-danger-500/10 cursor-pointer bg-transparent border-none"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
